import { useEffect, useRef, useState } from "react";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { firebaseAuth } from "../../lib/firebase";
import api, { formatApiErrorDetail } from "../../lib/api";

/**
 * Encapsulates the Firebase Phone-OTP login flow:
 *   1. build invisible reCAPTCHA (mounts to `recaptchaRef`)
 *   2. sendOtp(phone) → returns confirmation; advances step to "otp"; starts a 30s resend timer
 *   3. verifyOtp(otp) → advances step to "profile"
 *   4. completeSignin({ name, email }) → POSTs /auth/phone/firebase-session
 *
 * Behaviour identical to the previous monolithic component — extracted only.
 */
export function useFirebasePhoneAuth({ onSuccess, recaptchaRef }) {
  const [step, setStep] = useState("phone"); // "phone" | "otp" | "profile"
  const [phone, setPhone] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const verifierRef = useRef(null);

  // Resend countdown
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // Build invisible reCAPTCHA once
  useEffect(() => {
    if (!recaptchaRef.current || verifierRef.current) return undefined;
    try {
      verifierRef.current = new RecaptchaVerifier(firebaseAuth, recaptchaRef.current, {
        size: "invisible",
      });
    } catch {
      // Domain not whitelisted in Firebase / GCP — caught on send instead.
    }
    return () => {
      try { verifierRef.current?.clear(); } catch { /* noop */ }
      verifierRef.current = null;
    };
  }, [recaptchaRef]);

  const fullPhone = () => `+91${phone.replace(/\D/g, "").slice(-10)}`;

  const sendOtp = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) {
      setErr("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setErr("");
    setLoading(true);
    try {
      if (!verifierRef.current) {
        verifierRef.current = new RecaptchaVerifier(firebaseAuth, recaptchaRef.current, {
          size: "invisible",
        });
      }
      const result = await signInWithPhoneNumber(firebaseAuth, fullPhone(), verifierRef.current);
      setConfirmation(result);
      setStep("otp");
      setResendIn(30);
    } catch (e2) {
      setErr(_friendlyFirebaseErr(e2, () => {
        try { verifierRef.current?.clear(); } catch { /* noop */ }
        verifierRef.current = null;
      }));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (otp) => {
    if (otp.replace(/\D/g, "").length !== 6 || !confirmation) {
      setErr("Enter the 6-digit OTP");
      return;
    }
    setErr("");
    setLoading(true);
    try {
      await confirmation.confirm(otp.trim());
      setStep("profile");
    } catch (e2) {
      const code = e2?.code || "";
      if (code.includes("invalid-verification-code")) setErr("Wrong OTP");
      else if (code.includes("code-expired")) setErr("OTP expired. Request a new one.");
      else setErr(e2?.message || "Couldn't verify");
    } finally {
      setLoading(false);
    }
  };

  const completeSignin = async ({ name, email }) => {
    setErr("");
    setLoading(true);
    try {
      const idToken = await firebaseAuth.currentUser?.getIdToken();
      if (!idToken) throw new Error("Session lost — please retry");
      const { data } = await api.post("/auth/phone/firebase-session", {
        id_token: idToken,
        name: (name || "").trim() || null,
        email: (email || "").trim() || null,
      });
      onSuccess?.(data);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Server rejected session");
    } finally {
      setLoading(false);
    }
  };

  const goBackToPhone = () => {
    setStep("phone");
    setConfirmation(null);
  };

  return {
    step,
    phone, setPhone,
    err, loading, resendIn,
    sendOtp, verifyOtp, completeSignin,
    goBackToPhone,
  };
}

function _friendlyFirebaseErr(e, onCaptchaFail) {
  const code = e?.code || "";
  if (code.includes("too-many-requests")) return "Too many attempts. Try in 1 hour.";
  if (code.includes("invalid-phone-number")) return "Invalid phone number";
  if (code.includes("captcha-check-failed") || code.includes("argument-error")) {
    onCaptchaFail?.();
    return "reCAPTCHA failed. Please refresh and try again.";
  }
  return e?.message || "Couldn't send OTP";
}
